import React from "react";
import FilterableHeader from "./FilterableHeader";
import useFlotteFilters from "../useFlotteFilters";
import useStore from "../../../../../context/useStore";
import { columnsConfig } from "./flotteColumns.config";
import {
  getMouvementColorFromName,
  getGroupColorFromId,
  groupOptions,
} from "../FlotteSimulator/simulatorUtils";
import HideColumnIcon from "./HideColumnIcon";
import { StickyIdentiteHeader } from "./StickyIdentite";
import { useAtom } from "jotai";
import { connectedUserAtom } from "../../../../../middlewares/AuthMiddleware";
import { isVORole } from "./FlotteUtils";

const FlotteTableHeader = ({
  visibleHeaders,
  setVisibleHeaders,
  isAllRowsSelected,
  isSomeRowsSelected,
  toggleAllRowsSelection,
  stickyIdentite,
}) => {
  const [connectedUser] = useAtom(connectedUserAtom);
  const [state] = useStore();
  const { flotteFilters, handleChangeFilters, handleClearFilter } =
    useFlotteFilters();

  const handleFilterChange = (filterKey, value) => {
    handleChangeFilters({
      target: { name: filterKey, value, type: "filter" },
    });
  };

  const handleFilterClear = (filterKey) => {
    handleClearFilter(filterKey);
  };

  // Fonction pour obtenir les options dynamiquement
  const getFilterOptions = (optionsPath) => {
    if (optionsPath === "state.constant.items.FLOTTE.STATUTS_VEHICULE") {
      if (isVORole(connectedUser?.role)) {
        return state.constant.items.FLOTTE.STATUTS_VEHICULE_VO;
      }
      return state.constant.items.FLOTTE.STATUTS_VEHICULE;
    }
    if (optionsPath === "state.constant.items.CATALOG_HITECH") {
      return state?.constant?.items?.CATALOG_HITECH;
    }
    if (optionsPath === "state.constant.items.FLOTTE.TYPES_CLIENT") {
      return state?.constant?.items?.FLOTTE.TYPES_CLIENT;
    }
    if (optionsPath === "state.constant.items.FLOTTE.CARBURANTS") {
      return state?.constant?.items?.FLOTTE.CARBURANTS;
    }
    if (optionsPath === "state.constant.items.FLOTTE.TYPES_VEHICULE") {
      return state?.constant?.items?.FLOTTE.TYPES_VEHICULE;
    }
    if (optionsPath === "state.constant.items.FLOTTE.POSITIONS") {
      return state?.constant?.items?.FLOTTE.POSITIONS;
    }
    return undefined;
  };

  // Décoration d'entête selon le(s) groupe(s) d'affichage (LS-636) :
  // bandeau 3px en couleur pleine (ancre de teinte) + teinte de fond à 8 %.
  // "default" n'est jamais mis en avant (socle toujours visible).
  // La teinte est un backgroundImage : le background-color opaque du SCSS
  // doit rester en place (th sticky).
  const hexToRgba = (hex, alpha) => {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };

  const TINT_ALPHA = 0.08;

  const getGroupDecoration = (columnConfig) => {
    const displayGroups = (columnConfig.groups || []).filter(
      (g) => g !== "default",
    );
    if (displayGroups.length === 0) return null;

    const colors = displayGroups.map((g) => getGroupColorFromId(g));

    const barBackground =
      colors.length === 1
        ? colors[0]
        : `linear-gradient(90deg, ${colors
            .map((color, i) => {
              const start = (i * 100) / colors.length;
              const end = ((i + 1) * 100) / colors.length;
              return `${color} ${start}%, ${color} ${end}%`;
            })
            .join(", ")})`;

    const tints = colors.map((color) => hexToRgba(color, TINT_ALPHA));
    const tintImage =
      tints.length === 1
        ? `linear-gradient(${tints[0]}, ${tints[0]})`
        : `linear-gradient(180deg, ${tints
            .map((tint, i) => {
              const start = (i * 100) / tints.length;
              const end = ((i + 1) * 100) / tints.length;
              return `${tint} ${start}%, ${tint} ${end}%`;
            })
            .join(", ")})`;

    const title = displayGroups
      .map((g) => groupOptions.find((o) => o.id === g)?.name || g)
      .join(" / ");

    return { barBackground, tintImage, title, groups: displayGroups };
  };

  // Fonction pour masquer une colonne
  const handleHideColumn = (columnKey) => {
    // Ne pas permettre de masquer la colonne actions
    if (columnKey === "actions" || !setVisibleHeaders) return;

    setVisibleHeaders((prev) => ({
      ...prev,
      [columnKey]: false,
    }));
  };

  // Rendu d'un header filtrable ou simple
  const renderHeader = (columnKey, columnConfig) => {
    if (!visibleHeaders[columnKey]) return null;

    if (columnConfig.filterable && columnConfig.filterable !== false) {
      const { filterKey, filterType, placeholder, options, helpers } =
        columnConfig.filterable;

      return (
        <FilterableHeader
          title={columnConfig.title}
          filterKey={filterKey}
          filterType={filterType}
          currentValue={flotteFilters[filterKey]}
          options={options ? getFilterOptions(options) : undefined}
          placeholder={placeholder}
          helpers={helpers}
          onFilterChange={handleFilterChange}
          onFilterClear={handleFilterClear}
        />
      );
    } else {
      return null;
    }
  };

  return (
    <tr>
      {Object.entries(columnsConfig).map(([columnKey, columnConfig]) => {
        if (!visibleHeaders[columnKey]) return null;
        const decoration = getGroupDecoration(columnConfig);
        return (
          <React.Fragment key={columnKey}>
            <th
              title={decoration ? `Groupe(s) : ${decoration.title}` : undefined}
              style={{
                verticalAlign: "bottom",
                backgroundImage: decoration?.tintImage,
              }}
            >
              {decoration && (
                <div
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    height: 3,
                    background: decoration.barBackground,
                  }}
                />
              )}
              <div
                className="d-flex justify-content-between align-items-end"
                style={{ gap: 2 }}
              >
                <div>
                  {columnConfig.badge && (
                    <>
                      <span
                        className="badge badge-secondary mr-1 text-xs"
                        style={{
                          backgroundColor: getMouvementColorFromName(
                            columnConfig.badge,
                          ),
                          marginTop: 2,
                        }}
                      >
                        {columnConfig.badge}
                      </span>
                      <br />
                    </>
                  )}
                  {columnKey === "actions" && (
                    <input
                      type="checkbox"
                      className="mr-2"
                      checked={isAllRowsSelected?.() || false}
                      ref={(input) => {
                        if (input) {
                          input.indeterminate = isSomeRowsSelected?.() || false;
                        }
                      }}
                      onChange={() => toggleAllRowsSelection?.()}
                      onClick={(e) => e.stopPropagation()}
                      style={{ cursor: "pointer" }}
                    />
                  )}
                  {decoration && decoration.groups.length > 1 && (
                    <span
                      title={`Groupe(s) : ${decoration.title}`}
                      style={{ marginRight: 3 }}
                    >
                      {decoration.groups.map((g) => (
                        <span
                          key={g}
                          style={{
                            display: "inline-block",
                            width: 8,
                            height: 8,
                            borderRadius: "50%",
                            backgroundColor: getGroupColorFromId(g),
                            marginRight: 2,
                          }}
                        />
                      ))}
                    </span>
                  )}
                  <span
                    style={{
                      whiteSpace: "normal",
                      minWidth: 80,
                      fontSize: 12.5,
                      lineHeight: "13px",
                      display: "inline-block",
                    }}
                  >
                    {columnConfig.title}
                  </span>
                </div>
                <div className="d-flex align-items-center">
                  {renderHeader(columnKey, columnConfig)}
                  {columnKey !== "actions" && (
                    <HideColumnIcon
                      handleHideColumn={() => handleHideColumn(columnKey)}
                      columnConfig={columnConfig}
                    />
                  )}
                </div>
              </div>
            </th>
            {/* Colonne de synthèse figée (LS-707), juste après la colonne
              de sélection ("actions", jamais masquable) */}
            {columnKey === "actions" && stickyIdentite && (
              <StickyIdentiteHeader />
            )}
          </React.Fragment>
        );
      })}
    </tr>
  );
};

export default FlotteTableHeader;
